import type { SampleRegion } from "./brushes";
import type { VoxelField } from "./VoxelField";

/**
 * Topology regularisation.
 *
 * Surface Nets puts one vertex in every sign-changing cell and one quad around
 * every sign-changing sample edge. That is watertight, but two voxel patterns
 * make it non-manifold:
 *
 *   - A grid FACE whose four samples alternate in/out/in/out. The dual edge
 *     between the two cells sharing that face then carries four quads instead
 *     of two.
 *   - A CELL whose inside corners (or outside corners) fall into two separate
 *     groups. The single dual vertex then has to serve two sheets of surface.
 *
 * Both are ambiguities in the sampled data, not real features: they only occur
 * where a detail is finer than the voxel grid can represent. We resolve them by
 * pushing the offending sample to the outside, which removes at most a sliver
 * of sub-voxel material and can never add any — so the "carving only removes"
 * and "solid stays inside the blank" invariants hold, and the pass always
 * terminates because every step strictly reduces the number of inside samples.
 */

const CORNER_OFFSET: ReadonlyArray<readonly [number, number, number]> = [
  [0, 0, 0],
  [1, 0, 0],
  [0, 1, 0],
  [1, 1, 0],
  [0, 0, 1],
  [1, 0, 1],
  [0, 1, 1],
  [1, 1, 1],
];

/** The six cube faces, corners in cyclic order. */
const FACES: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 2, 6, 4],
  [1, 3, 7, 5],
  [0, 1, 5, 4],
  [2, 3, 7, 6],
  [0, 1, 3, 2],
  [4, 5, 7, 6],
];

function componentCount(mask: number, wanted: 0 | 1): number {
  const members: number[] = [];
  for (let c = 0; c < 8; c++) {
    if (((mask >> c) & 1) === wanted) members.push(c);
  }
  if (members.length === 0) return 0;

  let seen = 0;
  let components = 0;
  for (const start of members) {
    if ((seen >> start) & 1) continue;
    components++;
    const stack = [start];
    seen |= 1 << start;
    while (stack.length > 0) {
      const c = stack.pop() as number;
      for (let bit = 0; bit < 3; bit++) {
        const n = c ^ (1 << bit);
        if (((mask >> n) & 1) !== wanted) continue;
        if ((seen >> n) & 1) continue;
        seen |= 1 << n;
        stack.push(n);
      }
    }
  }
  return components;
}

/** Precomputed: does this cell configuration mesh to a manifold patch? */
const AMBIGUOUS = (() => {
  const table = new Uint8Array(256);
  for (let mask = 0; mask < 256; mask++) {
    if (mask === 0 || mask === 255) continue;
    let bad = false;
    for (const face of FACES) {
      let changes = 0;
      for (let n = 0; n < 4; n++) {
        const a = (mask >> (face[n] as number)) & 1;
        const b = (mask >> (face[(n + 1) % 4] as number)) & 1;
        if (a !== b) changes++;
      }
      if (changes === 4) {
        bad = true;
        break;
      }
    }
    if (!bad && (componentCount(mask, 1) > 1 || componentCount(mask, 0) > 1)) bad = true;
    table[mask] = bad ? 1 : 0;
  }
  return table;
})();

export function isAmbiguousMask(mask: number): boolean {
  return (AMBIGUOUS[mask] as number) === 1;
}

/**
 * Push ambiguous cells in `region` (sample indices) into unambiguous ones.
 * Returns the number of samples changed.
 */
export function regularizeRegion(field: VoxelField, region: SampleRegion): number {
  const { data, dirty, nx, ny, nz } = field;
  const nudge = field.h * 0.02;

  const ci0 = Math.max(0, region.i0 - 1);
  const cj0 = Math.max(0, region.j0 - 1);
  const ck0 = Math.max(0, region.k0 - 1);
  const ci1 = Math.min(nx - 1, region.i1 + 1);
  const cj1 = Math.min(ny - 1, region.j1 + 1);
  const ck1 = Math.min(nz - 1, region.k1 + 1);

  let totalChanged = 0;
  for (let pass = 0; pass < 12; pass++) {
    let changed = 0;
    for (let ck = ck0; ck < ck1; ck++) {
      for (let cj = cj0; cj < cj1; cj++) {
        for (let ci = ci0; ci < ci1; ci++) {
          let mask = 0;
          for (let c = 0; c < 8; c++) {
            const off = CORNER_OFFSET[c] as readonly [number, number, number];
            if ((data[field.index(ci + off[0], cj + off[1], ck + off[2])] as number) < 0) {
              mask |= 1 << c;
            }
          }
          if ((AMBIGUOUS[mask] as number) === 0) continue;

          // Evict the inside corner closest to the surface.
          let bestCorner = -1;
          let bestValue = -Infinity;
          let bestIndex = 0;
          for (let c = 0; c < 8; c++) {
            if (((mask >> c) & 1) === 0) continue;
            const off = CORNER_OFFSET[c] as readonly [number, number, number];
            const p = field.index(ci + off[0], cj + off[1], ck + off[2]);
            const v = data[p] as number;
            if (v > bestValue) {
              bestValue = v;
              bestCorner = c;
              bestIndex = p;
            }
          }
          if (bestCorner < 0) continue;
          data[bestIndex] = nudge;
          dirty[bestIndex] = 1;
          changed++;
        }
      }
    }
    totalChanged += changed;
    if (changed === 0) break;
  }
  return totalChanged;
}

/** Regularise the whole grid. Used before export. */
export function regularizeAll(field: VoxelField): number {
  return regularizeRegion(field, {
    i0: 0,
    i1: field.nx,
    j0: 0,
    j1: field.ny,
    k0: 0,
    k1: field.nz,
  });
}

/**
 * Relax signed-distance magnitudes around a surface without ever changing a
 * sample's inside/outside sign. This removes the voxel-scale corrugation that
 * topology regularisation can leave on a long cut face while preserving the
 * exact component topology it just made safe.
 */
export function relaxSurfaceBand(
  field: VoxelField,
  region: SampleRegion | null = null,
  passes = 4,
  blend = 0.62,
  allowOutsideBlank = false,
): number {
  const { data, dirty, nx, ny, nz } = field;
  const band = field.h * 2.75;
  const nudge = field.h * 0.025;
  const r = region ?? { i0: 0, i1: nx, j0: 0, j1: ny, k0: 0, k1: nz };
  const i0 = Math.max(1, r.i0 - 3);
  const i1 = Math.min(nx - 1, r.i1 + 3);
  const j0 = Math.max(field.baseCellJ + 1, r.j0 - 3);
  const j1 = Math.min(ny - 1, r.j1 + 3);
  const k0 = Math.max(1, r.k0 - 3);
  const k1 = Math.min(nz - 1, r.k1 + 3);
  let changed = 0;

  for (let pass = 0; pass < passes; pass++) {
    const src = new Float32Array(data);
    for (let k = k0; k < k1; k++) {
      const z = field.sampleZ(k);
      for (let j = j0; j < j1; j++) {
        const y = field.sampleY(j);
        for (let i = i0; i < i1; i++) {
          const p = field.index(i, j, k);
          const previous = src[p] as number;
          if (Math.abs(previous) > band) continue;

          const average =
            ((src[field.index(i - 1, j, k)] as number) +
              (src[field.index(i + 1, j, k)] as number) +
              (src[field.index(i, j - 1, k)] as number) +
              (src[field.index(i, j + 1, k)] as number) +
              (src[field.index(i, j, k - 1)] as number) +
              (src[field.index(i, j, k + 1)] as number) +
              previous * 2) /
            8;
          let next = previous + (average - previous) * blend;

          // Preserve the sign mask exactly, so manifold topology cannot
          // change. Also stay within the original blank and above its rear.
          next = previous < 0 ? Math.min(next, -nudge) : Math.max(next, nudge);
          if (!allowOutsideBlank) {
            const blank = field.blankDistance(field.sampleX(i), y, z);
            if (next < blank) next = blank;
          }
          next = field.clampValue(next);
          if (Math.abs(next - previous) < 1e-7) continue;
          data[p] = next;
          dirty[p] = 1;
          changed++;
        }
      }
    }
  }
  return changed;
}
